import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, bildLaden, fehlertext, type SessionUser } from "./api";
import { Auswahl, Eingabe, Fehler, Feld, Fotofeld, Karte, Knopf, Laedt, Leer, Seitenkopf } from "./ui";
import { sprache } from "./datum";

/**
 * Tankbuch.
 *
 * Vier Felder und ein Foto. Wer an einer windigen Tankstelle mit Handschuhen
 * ein Formular ausfuellen muss, fuellt es nicht aus, und dann fehlt der
 * Eintrag ganz.
 *
 * Der Kilometerstand steht oben, weil er der Wert ist, den man am Automaten
 * vergisst, sobald man weggefahren ist. Alles andere steht auf dem Beleg und
 * laesst sich nachtragen.
 */

interface Fahrzeug {
  id: string;
  kennzeichen: string;
  bezeichnung: string;
  modell: string | null;
  kraftstoff: string | null;
  aktiv: boolean;
}

interface Eintrag {
  id: string;
  fahrzeugId: string;
  fahrzeug: string;
  kennzeichen: string;
  benutzerId: string;
  mitarbeiter: string;
  zeitpunkt: string;
  liter: number | null;
  betragCent: number | null;
  kilometerstand: number | null;
  hatBeleg: boolean;
  notiz: string | null;
  /** Liter und Betrag stammen vom Belegfoto, nicht aus einer Eingabe. */
  vomBeleg: boolean;
  /** Leer heisst: der Beleg wurde noch nicht gelesen. */
  belegGelesenAm: string | null;
}

interface Liste {
  eintraege: Eintrag[];
  summeCent: number;
  summeLiter: number;
  /** Wie viele Belege noch keinen Betrag tragen. */
  ohneBetrag: number;
}

export function FuhrparkSeite({ user }: { user: SessionUser }) {
  const { t } = useTranslation();
  const istAdmin = user.role === "admin";

  const [fahrzeuge, setFahrzeuge] = useState<Fahrzeug[]>([]);
  const [liste, setListe] = useState<Liste | null>(null);
  const [monat, setMonat] = useState(() => new Date().toISOString().slice(0, 7));
  const [fehler, setFehler] = useState<string | null>(null);
  const [neu, setNeu] = useState(false);
  const [neuesFahrzeug, setNeuesFahrzeug] = useState(false);

  const laden = useCallback(async () => {
    const [j, m] = monat.split("-").map(Number);
    const von = `${monat}-01`;
    const bis = new Date(Date.UTC(j, m, 1)).toISOString().slice(0, 10);
    setListe(await api.get<Liste>(`/tankbuch?von=${von}&bis=${bis}`));
  }, [monat]);

  useEffect(() => {
    void laden().catch((e) => setFehler(fehlertext(e, t("fehler.laden"))));
  }, [laden, t]);

  useEffect(() => {
    void api.get<{ fahrzeuge: Fahrzeug[] }>("/fahrzeuge")
      .then((a) => setFahrzeuge(a.fahrzeuge))
      .catch(() => setFahrzeuge([]));
  }, [neuesFahrzeug]);

  return (
    <>
      <Seitenkopf
        titel={t("fuhrpark.titel")}
        hinweis={t("fuhrpark.hinweis")}
        aktionen={
          <>
            <Knopf disabled={fahrzeuge.length === 0} onClick={() => setNeu(true)}>
              {t("fuhrpark.neuerEintrag")}
            </Knopf>
            {istAdmin && (
              <Knopf art="neben" onClick={() => setNeuesFahrzeug(true)}>
                {t("fuhrpark.neuesFahrzeug")}
              </Knopf>
            )}
          </>
        }
      />
      <Fehler text={fehler} />

      {fahrzeuge.length === 0 && (
        <Karte className="border-l-4 border-marke p-4">
          <p className="text-sm">
            {istAdmin ? t("fuhrpark.keineFahrzeugeChef") : t("fuhrpark.keineFahrzeuge")}
          </p>
        </Karte>
      )}

      <div className="mt-4 flex flex-wrap items-end gap-4">
        <Feld label={t("zeit.monat")}>
          <Eingabe type="month" value={monat} onChange={(e) => setMonat(e.target.value)} />
        </Feld>
        {liste && (
          <>
            <p className="pb-2 text-sm">
              <span className="text-leise">{t("fuhrpark.summe")}: </span>
              <span className="text-lg font-bold">{euro(liste.summeCent)}</span>
              {/* Eine Summe, die stillschweigend unvollstaendig ist, waere
                  schlimmer als gar keine. */}
              {liste.ohneBetrag > 0 && (
                <span className="ms-2 text-xs text-verweis">
                  {t("fuhrpark.ohneBetrag", { count: liste.ohneBetrag })}
                </span>
              )}
            </p>
            <p className="pb-2 text-sm">
              <span className="text-leise">{t("fuhrpark.liter")}: </span>
              <span className="text-lg font-bold">{liste.summeLiter.toFixed(2)} l</span>
            </p>
          </>
        )}
      </div>

      {!liste ? <Laedt /> : liste.eintraege.length === 0 ? (
        <Leer text={t("fuhrpark.leer")} />
      ) : (
        <ul className="mt-3 space-y-2">
          {liste.eintraege.map((e) => (
            <Zeile key={e.id} eintrag={e} zeigeName={istAdmin} darfAendern={istAdmin} onGeaendert={() => void laden()} />
          ))}
        </ul>
      )}

      {neu && (
        <EintragDialog
          fahrzeuge={fahrzeuge.filter((f) => f.aktiv)}
          zuletzt={liste?.eintraege.find((e) => e.benutzerId === user.id)?.fahrzeugId ?? null}
          onClose={() => setNeu(false)}
          onFertig={() => { setNeu(false); void laden(); }}
        />
      )}
      {neuesFahrzeug && (
        <FahrzeugDialog
          onClose={() => setNeuesFahrzeug(false)}
          onFertig={() => setNeuesFahrzeug(false)}
        />
      )}
    </>
  );
}

function Zeile({
  eintrag, zeigeName, darfAendern, onGeaendert
}: {
  eintrag: Eintrag; zeigeName: boolean; darfAendern: boolean; onGeaendert: () => void;
}) {
  const { t } = useTranslation();
  const [beleg, setBeleg] = useState<string | null>(null);
  const [berichtigen, setBerichtigen] = useState(false);
  const [laeuft, setLaeuft] = useState(false);

  /** Den Beleg noch einmal lesen lassen, etwa nach einem aufgebrauchten Budget. */
  async function nochmalLesen() {
    setLaeuft(true);
    try {
      await api.post(`/tankbuch/${eintrag.id}/beleg-lesen`);
      onGeaendert();
    } catch {
      // Der Grund steht im Postprotokoll. Hier hilft nur ein zweiter Versuch
      // oder das Abtippen, und beides steht ohnehin daneben.
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <li className="rounded-card bg-white p-4">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="font-semibold tabular-nums">{datum(eintrag.zeitpunkt)}</span>
        <span className="font-semibold">{eintrag.fahrzeug}</span>
        <span className="text-sm text-leise">{eintrag.kennzeichen}</span>
        {zeigeName && <span className="text-sm text-leise">{eintrag.mitarbeiter}</span>}
        <span className="ms-auto text-lg font-bold tabular-nums">
          {eintrag.betragCent === null
            ? <span className="text-sm font-normal text-leise">{t("fuhrpark.betragOffen")}</span>
            : euro(eintrag.betragCent)}
        </span>
      </div>
      <p className="mt-1 text-sm text-leise">
        {[
          eintrag.liter !== null ? `${eintrag.liter.toFixed(2)} l` : null,
          eintrag.kilometerstand !== null
            ? t("fuhrpark.kmStand", { km: eintrag.kilometerstand.toLocaleString() })
            : null,
          // Woher die Zahlen stammen. Ein vom Foto gelesener Wert ist so gut
          // wie das Foto, und wer eine krumme Zahl sieht, soll wissen, wo er
          // nachsehen muss.
          eintrag.vomBeleg ? t("fuhrpark.vomBeleg") : null,
          eintrag.notiz
        ].filter(Boolean).join(" · ")}
      </p>
      {eintrag.hatBeleg && (
        beleg ? (
          <img src={beleg} alt="" className="mt-2 max-h-96 rounded" />
        ) : (
          <button
            className="mt-2 text-sm text-verweis underline"
            onClick={() => void bildLaden(`/tankbuch/${eintrag.id}/beleg`).then(setBeleg).catch(() => {})}
          >
            {t("fuhrpark.belegZeigen")}
          </button>
        )
      )}

      {darfAendern && (
        <div className="mt-2 flex flex-wrap gap-3 text-sm">
          <button className="text-verweis underline" onClick={() => setBerichtigen(true)}>
            {t("fuhrpark.berichtigen")}
          </button>
          {/* Nur anbieten, wenn noch nichts dasteht. Ein zweiter Lesevorgang
              ueber einer berichtigten Zahl waere Geld fuer nichts. */}
          {eintrag.betragCent === null && eintrag.hatBeleg && (
            <button
              className="text-verweis underline disabled:opacity-50"
              disabled={laeuft}
              onClick={() => void nochmalLesen()}
            >
              {t("fuhrpark.nochmalLesen")}
            </button>
          )}
        </div>
      )}

      {berichtigen && (
        <BerichtigenDialog
          eintrag={eintrag}
          onClose={() => setBerichtigen(false)}
          onFertig={() => { setBerichtigen(false); onGeaendert(); }}
        />
      )}
    </li>
  );
}

/**
 * Liter, Betrag und Kilometerstand berichtigen.
 *
 * Der Gegenpol zur Belegauswertung: was vom Foto gelesen wurde, ist ein
 * Vorschlag. Wer hier etwas eintraegt, hat das letzte Wort, und der naechste
 * Lesevorgang laesst die Zahl in Ruhe.
 */
function BerichtigenDialog({
  eintrag, onClose, onFertig
}: { eintrag: Eintrag; onClose: () => void; onFertig: () => void }) {
  const { t } = useTranslation();
  const [liter, setLiter] = useState(eintrag.liter === null ? "" : String(eintrag.liter));
  const [betrag, setBetrag] = useState(
    eintrag.betragCent === null ? "" : (eintrag.betragCent / 100).toFixed(2)
  );
  const [km, setKm] = useState(
    eintrag.kilometerstand === null ? "" : String(eintrag.kilometerstand)
  );
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  async function speichern() {
    setLaeuft(true);
    setFehler(null);
    try {
      await api.patch(`/tankbuch/${eintrag.id}`, {
        liter: liter === "" ? null : Number(liter),
        betrag: betrag === "" ? null : Number(betrag),
        kilometerstand: km === "" ? null : Number(km)
      });
      onFertig();
    } catch (e) {
      setFehler(fehlertext(e, t("fehler.speichern")));
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <Dialog titel={t("fuhrpark.berichtigen")} onClose={onClose}>
      <Fehler text={fehler} />
      <div className="grid gap-3 sm:grid-cols-3">
        <Feld label={t("fuhrpark.literLabel")}>
          <Eingabe
            type="number" inputMode="decimal" step="0.01" min={0}
            value={liter} onChange={(e) => setLiter(e.target.value)}
          />
        </Feld>
        <Feld label={t("fuhrpark.betrag")}>
          <Eingabe
            type="number" inputMode="decimal" step="0.01" min={0}
            value={betrag} onChange={(e) => setBetrag(e.target.value)}
          />
        </Feld>
        <Feld label={t("fuhrpark.kilometerstand")}>
          <Eingabe
            type="number" inputMode="numeric" min={0}
            value={km} onChange={(e) => setKm(e.target.value)}
          />
        </Feld>
      </div>
      <div className="mt-5 flex gap-2">
        <Knopf disabled={laeuft} onClick={() => void speichern()}>{t("aktion.speichern")}</Knopf>
        <Knopf art="neben" onClick={onClose}>{t("aktion.abbrechen")}</Knopf>
      </div>
    </Dialog>
  );
}

/**
 * Tanken eintragen: drei Angaben.
 *
 * Fahrzeug, Kilometerstand, Foto vom Beleg. Liter, Betrag und Notiz gab es
 * hier frueher auch, und der Betrag war Pflicht. An einer windigen Tankstelle
 * mit Handschuhen wird eine Maske mit sechs Feldern nicht ungenau ausgefuellt,
 * sondern gar nicht.
 *
 * Liter und Betrag stehen auf dem Beleg, den er ohnehin fotografiert, und
 * werden daraus gelesen. Im Buero lassen sie sich berichtigen.
 */
function EintragDialog({
  fahrzeuge, zuletzt, onClose, onFertig
}: {
  fahrzeuge: Fahrzeug[];
  /** Das zuletzt von diesem Mitarbeiter betankte Fahrzeug, falls bekannt. */
  zuletzt: string | null;
  onClose: () => void;
  onFertig: () => void;
}) {
  const { t } = useTranslation();
  // Vorbelegt mit dem zuletzt benutzten Fahrzeug. Wer tauscht, stellt um.
  const [fahrzeugId, setFahrzeugId] = useState(
    zuletzt && fahrzeuge.some((f) => f.id === zuletzt) ? zuletzt : (fahrzeuge[0]?.id ?? "")
  );
  const [km, setKm] = useState("");
  const [datei, setDatei] = useState<File | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  async function speichern() {
    setLaeuft(true);
    setFehler(null);
    try {
      const form = new FormData();
      form.append("fahrzeugId", fahrzeugId);
      form.append("kilometerstand", km);
      if (datei) form.append("datei", datei);
      await api.request("/tankbuch", { method: "POST", body: form });
      onFertig();
    } catch (e) {
      setFehler(fehlertext(e, t("fehler.speichern")));
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <Dialog titel={t("fuhrpark.neuerEintrag")} onClose={onClose}>
      <Fehler text={fehler} />
      <p className="mb-3 text-sm text-leise">{t("fuhrpark.dreiAngaben")}</p>
      <div className="grid gap-3">
        <Feld label={t("fuhrpark.fahrzeug")} breit>
          <Auswahl value={fahrzeugId} onChange={(e) => setFahrzeugId(e.target.value)}>
            {fahrzeuge.map((f) => (
              <option key={f.id} value={f.id}>{f.bezeichnung} · {f.kennzeichen}</option>
            ))}
          </Auswahl>
        </Feld>
        <Feld label={t("fuhrpark.kilometerstand")} hinweis={t("fuhrpark.kmHinweis")} breit>
          <Eingabe
            type="number" inputMode="numeric" min={0}
            value={km} onChange={(e) => setKm(e.target.value)}
          />
        </Feld>
        <Feld label={t("fuhrpark.beleg")} hinweis={t("fuhrpark.belegPflicht")} breit>
          <Fotofeld datei={datei} onDatei={setDatei} pflicht beschriftung={t("foto.belegAufnehmen")} />
        </Feld>
      </div>
      <div className="mt-5 flex gap-2">
        <Knopf disabled={laeuft || !fahrzeugId || !km || !datei} onClick={() => void speichern()}>
          {t("aktion.speichern")}
        </Knopf>
        <Knopf art="neben" onClick={onClose}>{t("aktion.abbrechen")}</Knopf>
      </div>
    </Dialog>
  );
}

function FahrzeugDialog({ onClose, onFertig }: { onClose: () => void; onFertig: () => void }) {
  const { t } = useTranslation();
  const [kennzeichen, setKennzeichen] = useState("");
  const [bezeichnung, setBezeichnung] = useState("");
  const [modell, setModell] = useState("");
  const [kraftstoff, setKraftstoff] = useState("");
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  async function speichern() {
    setLaeuft(true);
    setFehler(null);
    try {
      await api.post("/fahrzeuge", {
        kennzeichen, bezeichnung, modell: modell || null, kraftstoff: kraftstoff || null
      });
      onFertig();
    } catch (e) {
      setFehler(fehlertext(e, t("fehler.speichern")));
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <Dialog titel={t("fuhrpark.neuesFahrzeug")} onClose={onClose}>
      <Fehler text={fehler} />
      <div className="grid gap-3 sm:grid-cols-2">
        <Feld label={t("fuhrpark.bezeichnung")} hinweis={t("fuhrpark.bezeichnungHinweis")}>
          <Eingabe value={bezeichnung} onChange={(e) => setBezeichnung(e.target.value)} />
        </Feld>
        <Feld label={t("fuhrpark.kennzeichen")}>
          <Eingabe value={kennzeichen} onChange={(e) => setKennzeichen(e.target.value)} />
        </Feld>
        <Feld label={t("fuhrpark.modell")}>
          <Eingabe value={modell} onChange={(e) => setModell(e.target.value)} />
        </Feld>
        <Feld label={t("fuhrpark.kraftstoff")}>
          <Eingabe value={kraftstoff} onChange={(e) => setKraftstoff(e.target.value)} />
        </Feld>
      </div>
      <div className="mt-5 flex gap-2">
        <Knopf disabled={laeuft || !kennzeichen || !bezeichnung} onClick={() => void speichern()}>
          {t("aktion.speichern")}
        </Knopf>
        <Knopf art="neben" onClick={onClose}>{t("aktion.abbrechen")}</Knopf>
      </div>
    </Dialog>
  );
}

function Dialog({
  titel, children, onClose
}: { titel: string; children: React.ReactNode; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/40 p-4">
      <div className="max-h-full w-full max-w-2xl overflow-y-auto rounded-card bg-white p-6">
        <h2 className="mb-4 text-lg font-bold">{titel}</h2>
        {children}
        <button className="sr-only" onClick={onClose}>schliessen</button>
      </div>
    </div>
  );
}

function euro(cent: number): string {
  return `${(cent / 100).toFixed(2)} €`;
}

function datum(iso: string): string {
  return new Date(iso).toLocaleDateString(sprache(), {
    day: "2-digit", month: "2-digit", year: "2-digit"
  });
}
